import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import {
  type EmploymentStatus,
  type Membership,
  membershipsApi,
} from '../../services/memberships.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { SalesAccessCard } from './SalesAccessCard.js';
import { ShareLinkCard } from './ShareLinkCard.js';

// 046: membership detail — identity read panel (user/org/role are immutable;
// delete + re-invite to change) + admin edit form for the PATCH whitelist.
// Country cleared → explicit assigned_country_id: null so the backend blanks
// the denormalized name too (043 stale-name lesson).

const STATUSES: EmploymentStatus[] = ['active', 'inactive', 'terminated'];

interface GeoOption {
  id: string;
  name: string;
}

export function MembershipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const ability = useAbility();
  const canManage = ability.can('manage', 'Membership');
  const queryClient = useQueryClient();

  const { data: membership, isLoading } = useQuery<Membership>({
    queryKey: RESOURCE_KEYS.membership.one(id as string),
    queryFn: () => membershipsApi.get(id as string),
    enabled: Boolean(id),
  });

  const { data: countriesData } = useQuery<{ items: GeoOption[] }>({
    queryKey: ['geo-countries'],
    queryFn: () => api.get('/geo/countries'),
    enabled: canManage,
  });

  const [employeeCode, setEmployeeCode] = useState('');
  const [status, setStatus] = useState<EmploymentStatus>('active');
  const [countryId, setCountryId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!membership) return;
    setEmployeeCode(membership.employee_code ?? '');
    setStatus(membership.employment_status);
    setCountryId(membership.assigned_country?.id ?? '');
    setNotes(membership.notes ?? '');
  }, [membership]);

  async function save() {
    if (!id) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // whitelist only; empty string → explicit null (clears server-side)
      await membershipsApi.update(id, {
        employee_code: employeeCode.trim() === '' ? null : employeeCode.trim(),
        employment_status: status,
        assigned_country_id: countryId === '' ? null : countryId,
        notes: notes.trim() === '' ? null : notes,
      });
      await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.one(id as string) });
      await queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.membership.list });
      setSaved(true);
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading || !membership) {
    return (
      <div>
        <PageHeader title="Membership" fallback="/memberships" />
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Membership" fallback="/memberships" />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Identity</h2>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-steel">Employee</dt>
              <dd className="text-ink">
                {membership.user ? (
                  <>
                    <div>{membership.user.name || '—'}</div>
                    <div className="text-xs text-steel">{membership.user.email}</div>
                  </>
                ) : (
                  <span className="italic text-steel">(deleted user)</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-steel">Organization</dt>
              <dd className="text-ink">
                {membership.supplier?.company_name ?? (
                  <span className="italic text-steel">(deleted supplier)</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-steel">Role</dt>
              <dd className="text-ink">{membership.role}</dd>
            </div>
            {membership.deleted_at && (
              <div>
                <dt className="text-steel">Deleted</dt>
                <dd className="text-red-600">
                  {new Date(membership.deleted_at).toLocaleString('en-MY')}
                </dd>
              </div>
            )}
            {!canManage && (
              <>
                <div>
                  <dt className="text-steel">Employee code</dt>
                  <dd className="text-ink">{membership.employee_code ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-steel">Status</dt>
                  <dd className="text-ink">{membership.employment_status}</dd>
                </div>
                <div>
                  <dt className="text-steel">Country</dt>
                  <dd className="text-ink">{membership.assigned_country?.name ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-steel">Notes</dt>
                  <dd className="text-ink">{membership.notes ?? '—'}</dd>
                </div>
              </>
            )}
          </dl>
        </Card>

        {canManage && !membership.deleted_at && (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Employment record</h2>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <FormField label="Employee code" name="employee_code">
                <Input
                  id="employee_code"
                  name="employee_code"
                  maxLength={100}
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                />
              </FormField>
              <FormField label="Status" name="employment_status">
                <Select
                  id="employment_status"
                  name="employment_status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EmploymentStatus)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Country" name="assigned_country_id">
                <Select
                  id="assigned_country_id"
                  name="assigned_country_id"
                  value={countryId}
                  onChange={(e) => setCountryId(e.target.value)}
                >
                  <option value="">— none —</option>
                  {(countriesData?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Notes" name="notes">
                <Textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  maxLength={1000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormField>
              {error && <p className="text-sm text-red-600">{error}</p>}
              {saved && !error && <p className="text-sm text-green-700">Saved.</p>}
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </form>
          </Card>
        )}
      </div>
      {/* 343 (#937): a Host's share link and QR code. */}
      {id && !membership.deleted_at && (
        <div className="mt-6">
          <ShareLinkCard membershipId={id} membershipRole={membership.role} />
          {/* 377 (#1233): who may see the supplier's sales. */}
          <div className="mt-6">
            <SalesAccessCard
              membershipId={id}
              membershipRole={membership.role}
              supplierName={membership.supplier?.company_name ?? ''}
              canSeeSales={membership.can_see_sales === true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
